/**
 * Binds the shared mux WebSocket to the shell's UI language. A browser cannot
 * give a WebSocket handshake headers, so the language rides on the URL as
 * `?lang=<locale>`; the backend renders its error messages in it. A language
 * switch while the socket is connecting or open reconnects it ONCE on the new
 * URL — channels, subscriptions and queued frames stay with the client.
 */
import { watch } from 'vue';
import { withLocaleQuery } from '@blpde/connect/http';
import { useShellLocale } from '@/settings';

/** The slice of the mux client this binding drives. */
export interface LocaleBoundSocket {
  url: string;
  ws: unknown;
  isReady(): boolean;
  reauth(): boolean;
  stop(): void;
  connect(): void;
  setUrlProvider(fn: () => string): void;
}

/**
 * Sets `socket.url` to `buildUrl()` carrying the current UI language and keeps
 * it current; `buildUrl` is also read again on every connect. Returns the function that detaches the binding.
 */
export function bindSocketLocale(socket: LocaleBoundSocket, buildUrl: () => string): () => void {
  const { locale } = useShellLocale();
  socket.url = withLocaleQuery(buildUrl(), locale.value);
  // Every (re)connect re-reads the URL, so it always follows the API base the
  // HTTP client uses now — never a value captured before the user picked a
  // server on the login page (I-2833).
  socket.setUrlProvider(() => withLocaleQuery(buildUrl(), locale.value));
  return watch(locale, (next) => {
    const url = withLocaleQuery(buildUrl(), next);
    if (url === socket.url) return;
    socket.url = url;
    // No connection yet (or one waiting out a backoff): the next connect()
    // already dials the new URL.
    if (!socket.ws) return;
    // Open and handshaken: reauth() closes it and dials again at once.
    // Still connecting: stop() drops the half-open socket, connect() redials.
    if (!socket.reauth()) {
      socket.stop();
      socket.connect();
    }
  });
}
